import {
  ComponentSeries,
  ExamDate,
  SubmissionWindow,
  ReplacementFilesComponentsDto,
  BusinessUnit,
  Series,
} from '@cambridgeassessment/lib-ee-commons';
import { Moment } from 'moment';
import qs from 'qs';
import { post, patch, httpDelete, collectPagedResource, get } from '../httpHelpers/primer';
import { get as reporterGet } from '../httpHelpers/reporter';
import * as submitter from '../httpHelpers/submitter';
import { collectPagedResource as getPagedResource, get as entryGet } from '../httpHelpers/papiEntree';
import { ComponentsData, Section, LocalSection, Component, FileType, ComponentSectionDoc } from './types';
import { FindComponentsWithSubmittedSectionsQuery } from '@cambridgeassessment/lib-ee-commons/src/domain/external/dto/submitter';

const seriesIdsToUrlParams = (seriesIds: number[]): string => {
  const seriesIdsParam = new URLSearchParams();
  seriesIds.forEach((seriesId) => seriesIdsParam.append('seriesId', seriesId.toString()));

  return seriesIdsParam.toString();
};

export const getComponentByIdentifiers = (
  businessUnitId: string,
  assessmentCode: string,
  componentNumber: string,
  qualificationId = ' ',
): Promise<Component> =>
  get(
    `v1/businessUnit/${businessUnitId}/qualifications/${qualificationId}/assessments/${assessmentCode}/components/${componentNumber}`,
  );

export const getComponentData = (componentId: string): Promise<Component> => get(`v1/id/components/${componentId}`);

export const getComponentWithEntriesCheck = (
  businessUnitId: string,
  seriesIds: number[],
  componentId: string,
): Promise<Component> =>
  entryGet(
    `v1/businessUnit/${businessUnitId}/components/${componentId}?${decodeURIComponent(
      seriesIdsToUrlParams(seriesIds),
    )}`,
  );

export const getComponentsWithEntriesCheck = (
  businessUnitId: string,
  seriesIds: number[],
  qualificationId: number,
  assessmentCode: string,
  version: number,
): Promise<ComponentsData> => {
  const PAGE_SIZE = 10;
  const seriesIdsParam = new URLSearchParams();
  seriesIds.forEach((seriesId) => seriesIdsParam.append('seriesId', seriesId.toString()));

  return getPagedResource(
    `v1/businessUnit/${businessUnitId}/components/series/qualifications/${qualificationId}/assessments/${assessmentCode}/version/${version}?${decodeURIComponent(
      seriesIdsToUrlParams(seriesIds),
    )}`,
    PAGE_SIZE,
  );
};

export const getComponentsData = (
  businessUnitId: string,
  qualificationId: number,
  assessmentCode: string,
  version: number,
): Promise<ComponentsData> =>
  collectPagedResource(
    `v1/businessUnit/${businessUnitId}/qualifications/${qualificationId}/assessments/${assessmentCode}/version/${version}/components`,
  );

export const updateAdditionalFileTypes = (
  componentId: Component['_id'],
  value: Component['additionalFileTypes'],
): Promise<Component> => patch(`v1/id/components/${componentId}/additionalFileTypes`, { value });

export const createSection = (componentId: string, section: LocalSection): Promise<Component> =>
  post(`v1/id/components/${componentId}/sections`, section);

export const createComponentSection = (
  componentId: string,
  componentSection: ComponentSectionDoc,
): Promise<Component> => post(`v1/id/components/${componentId}/componentSections`, componentSection);

export const addExamDate = (
  componentId: string,
  seriesId: ComponentSeries['id'],
  examDate: ExamDate,
): Promise<Component> => post(`v1/id/components/${componentId}/series/${seriesId}/examDates`, examDate);

export const updateComponent = (
  componentId: Component['_id'],
  value: Component[keyof Component],
  field: keyof Component,
): Promise<Component> => patch(`v1/id/components/${componentId}?field=${field}`, { value });

export const updateSection = (
  componentId: string,
  value: string | number | boolean | FileType[] | undefined,
  field: string,
  id: number,
): Promise<Component> => patch(`v1/id/components/${componentId}/sections?id=${id}&field=${field}`, { value });

export const updateComponentSection = (
  componentId: string,
  value: string | number | boolean | FileType[] | undefined,
  field: string,
  id: number,
): Promise<Component> => patch(`v1/id/components/${componentId}/componentSections?id=${id}&field=${field}`, { value });

export const updateSeries = (
  componentId: string,
  value: string | number | boolean | Moment | undefined | SubmissionWindow | ExamDate[],
  field: string,
  id: number,
): Promise<Component> => patch(`v1/id/components/${componentId}/series?id=${id}&field=${field}`, { value });

export const updatePlagiarismFlag = (
  componentId: string,
  value: string | number | boolean | FileType[] | undefined,
  field: string,
  id: number,
): Promise<Component> =>
  patch(`v1/id/components/${componentId}/sections/plagiarism?id=${id}&field=${field}`, { value });

export const deleteSection = <T>(componentId: string, id: number): Promise<T | undefined> =>
  httpDelete(`v1/id/components/${componentId}/sections?id=${id}`);

export const deleteComponentSection = <T>(componentId: string, id?: number): Promise<T | undefined> => {
  const query = qs.stringify({ id });
  return httpDelete(`v1/id/components/${componentId}/componentSections?${query}`);
};

export const deleteSeriesSubmissionWindow = <T>(
  componentId: string,
  id: number,
  field: 'submissionWindow' | 'marksSubmissionWindow',
): Promise<T | undefined> => httpDelete(`v1/id/components/${componentId}/series?id=${id}&field=${field}`);

export const createDigitallyReceivedFlag = (
  componentId: string,
  sectionData: Section | LocalSection,
): Promise<Component> => post(`v1/id/components/${componentId}/sections`, sectionData);

export const deleteDigitallyReceivedFlag = <T>(componentId: string): Promise<T | undefined> =>
  httpDelete(`v1/id/components/${componentId}/sections`);

export const getComponentsWithSubmittedSections = (
  businessUnitId: string,
  seriesId: number,
  centreId: string,
  learnerNumber?: FindComponentsWithSubmittedSectionsQuery['learnerNumber'] | null,
  assessmentCode?: FindComponentsWithSubmittedSectionsQuery['assessmentCode'] | null,
  componentNumber?: FindComponentsWithSubmittedSectionsQuery['componentNumber'] | null,
  sortBy?: FindComponentsWithSubmittedSectionsQuery['sortBy'],
  order?: FindComponentsWithSubmittedSectionsQuery['order'],
): Promise<ReplacementFilesComponentsDto[]> => {
  const path = `v1/businessUnit/${businessUnitId}/components/centres/${centreId}/series/${seriesId}/componentsWithSubmittedSections`;

  const queryParams = [];
  if (learnerNumber) {
    queryParams.push(`learnerNumber=${encodeURIComponent(learnerNumber)}`);
  }

  if (assessmentCode) {
    queryParams.push(`assessmentCode=${encodeURIComponent(assessmentCode)}`);
  }

  if (componentNumber) {
    queryParams.push(`componentNumber=${encodeURIComponent(componentNumber)}`);
  }

  if (sortBy) {
    queryParams.push(`sortBy=${encodeURIComponent(sortBy)}`);
  }

  if (order) {
    queryParams.push(`order=${encodeURIComponent(order)}`);
  }

  if (queryParams.length > 0) {
    const pathWithQuery = `${path}?${queryParams.join('&')}`;
    return submitter.get(pathWithQuery);
  }

  return submitter.get(path);
};

export const downloadComponentRulesReport = async (businessUnitId: BusinessUnit['id']): Promise<Blob> =>
  reporterGet(`v1/${businessUnitId}/component-business-rules`);

export const updateSeriesStartDateByComponentSeries = (componentId: Component['_id']): Promise<void> =>
  post(`v1/id/components/${componentId}/updateSeriesStart`, {});

export const setDefaultSalgoStartDate = (
  componentId: string,
  seriesId: Series['id'],
): Promise<{ defaultStartDate: string }> =>
  patch(`v1/id/components/${componentId}/series/${seriesId}/default-salgo-start-date`, {});
